/**
 * CameraMath — pure, side-effect-free math helpers for CameraSystem.
 *
 * All functions are exported individually so they can be unit-tested
 * without any Babylon.js or DOM dependency.
 *
 * Read-only invariant (structural): callers MUST NOT pass mutable game
 * objects into these functions — they are pure numeric transforms. The
 * corresponding behavioral invariant in CameraSystem.update(dt) is that
 * it reads physics bodies via body.getLinearVelocity() (read-only) and
 * never calls any setter on bodies or meshes.
 */

/**
 * Frame-rate-independent lerp factor.
 *
 * Given a time-constant `k` (in 1/seconds) and a frame delta `dt` (in
 * seconds), returns the lerp alpha for this frame so that the smoothing
 * converges at the same rate regardless of frame rate.
 *
 * Formula: 1 - exp(-k * dt)
 *
 * - At dt = 1/60 and k = 6: alpha ≈ 0.095 (≈ k*dt for small dt)
 * - Two half-steps equal one full-step: (see camera-math.spec.ts)
 */
export function frameRateIndependentLerpFactor(k: number, dt: number): number {
  return 1 - Math.exp(-k * dt);
}

/**
 * Lookahead offset for the camera X position.
 *
 * Scales the average horizontal velocity by `scale`, then clamps the
 * result to ±`max`. When the cats are moving right the camera nudges
 * ahead of the midpoint so the player can see where they are going.
 *
 * @param avgVx  Average horizontal velocity of all targets (world units/s)
 * @param scale  Multiplier — tune to control how far ahead the camera peeks
 * @param max    Maximum lookahead offset in world units
 */
export function lookaheadOffset(avgVx: number, scale: number, max: number): number {
  return Math.max(-max, Math.min(max, avgVx * scale));
}

/**
 * Apply a deadzone to a 1-D follow target.
 *
 * If the target is within `dz` of `current`, return `current` unchanged
 * (no movement). Otherwise return `target`. This prevents small jitter
 * or jump oscillations from pulling the camera constantly.
 *
 * @param current  Current camera position on this axis
 * @param target   Desired camera position on this axis
 * @param dz       Half-width of the dead zone (world units)
 */
export function applyDeadzone(current: number, target: number, dz: number): number {
  if (Math.abs(target - current) <= dz) {
    return current;
  }
  return target;
}

/**
 * Decay a shake magnitude toward 0 linearly.
 *
 * Subtracts `decayPerSec * dt` from `mag`, clamping at 0.
 * Linear decay is intentional — it gives a constant-rate fade so the
 * shake feels the same at all frame rates and is trivially frame-rate
 * independent (total decay = decayPerSec * totalDt regardless of
 * how many frames cover that totalDt).
 *
 * @param mag         Current shake magnitude (world units)
 * @param dt          Frame delta (seconds)
 * @param decayPerSec How many world-unit magnitude units to shed per second
 */
export function shakeDecay(mag: number, dt: number, decayPerSec: number): number {
  return Math.max(0, mag - decayPerSec * dt);
}

/**
 * Decay a focus-bias weight from its current value toward 0.
 *
 * Used for the Tab-refocus effect: when the player switches cats, a
 * temporary focus weight nudges the camera toward the newly-active cat.
 * The weight decays linearly at rate 1/dur (full weight gone after
 * `dur` seconds of continuous dt steps).
 *
 * Frame-rate independent: weight -= (w_initial / dur) * dt per frame,
 * so ten 1/60 steps equal one 10/60 step for any initial weight.
 *
 * Note: `dur` here is NOT the total elapsed time — it is the *duration
 * constant* representing how fast the weight decays in seconds. Pass the
 * *original* weight (not the current one) separately if you need
 * absolute timing; for the camera use case the decrement is proportional
 * to the current weight so the caller accumulates the decrement.
 *
 * Implementation choice: linear decay with unit rate means `w - dt/dur`
 * clamped to 0. This keeps the arithmetic trivially frame-rate
 * independent (sum of decrements = totalDt / dur) regardless of step
 * count.
 *
 * @param w    Current focus weight [0..1]
 * @param dt   Frame delta (seconds)
 * @param dur  Total duration over which weight should decay from 1 to 0 (seconds)
 */
export function focusWeightDecay(w: number, dt: number, dur: number): number {
  if (dur <= 0) return 0;
  return Math.max(0, w - dt / dur);
}
